import { PlusOutlined, LoadingOutlined, EditTwoTone, EyeFilled } from "@ant-design/icons";
import { Button, Col, DatePicker, Row, Spin, Table } from "antd";
import React from "react";
import CTConfiguration, { CTConfigurationBase } from "../../Services/CTConfigurationService";
import dayjs, { Dayjs } from "dayjs";
import AddRnDRates from "./AddRnDRates";
import { adminContext } from "../../Common/PageRoute";
import Link from "antd/es/typography/Link";
import { useUser } from "../../Context/UserContext";
import { ColumnsType } from "antd/es/table";
import CustomRangePicker from "../../Common/Custom Components/Ant design/RangePicker";

interface CTConfigurationRnDRate extends CTConfigurationBase {
  id?: string;
  smeRate: number;
  lcsRate: number;
  smeVaccRate?: number;
  lcsVaccRate: number;
  minRate?: number;
  minAmount?: number;
  taxRate: number;
  lTaxRate: number;
}

const RnDRates = () => {
  const { openNotification } = React.useContext(adminContext);
  const [rnDRatesList, setRnDRates] = React.useState<CTConfigurationRnDRate[]>([]);
  const [filterdata, setFilterData] = React.useState<CTConfigurationRnDRate[]>([]);
  const [dialog, setDialog] = React.useState<{ type: string, params: any }>({ type: "", params: {} });
  const [loading, setLoading] = React.useState(false);
  const [refresh, setRefresh] = React.useState(true);
  const [params, setParams] = React.useState<{
    fromDate: Dayjs | null,
    toDate: Dayjs | null
  }
  >({
    fromDate: null,
    toDate: null,
  })
  const [pagination, setPagination] = React.useState({
    current: 1,
    pageSize: 15,
  });
  const { isSubAdmin } = useUser();

  const columns: ColumnsType<CTConfigurationRnDRate> = [
    {
      title: "S.No",
      dataIndex: "sno",
      key: "sno",
      width: "3%",
      render: (_: any, record: any, index: any) => <p>{(pagination.current - 1) * pagination.pageSize + index + 1}</p>,
    },
    {
      title: "Start date",
      dataIndex: "startDate",
      key: "startDate",
      width: "8%",
      render: (_: any, record: any, index: any) => <p>{dayjs(record.startDate).format("DD/MM/YYYY")}</p>,
      sorter: (a, b) =>
        dayjs(a.startDate).unix() - dayjs(b.startDate).unix(),
    },
    {
      title: "SME RnD relief rate",
      dataIndex: "smeRate",
      key: "smeRate",
      width: "12%",
      sorter: (a, b) => a.smeRate - b.smeRate,
    },
    {
      title: "SME vacc RnD relief rate",
      dataIndex: "smeVaccRate",
      key: "smeVaccRate",
      width: "15%",
      sorter: (a, b) => (a.smeVaccRate ?? 0) - (b.smeVaccRate ?? 0),
    },
    {
      title: "SME tax rate",
      dataIndex: "taxRate",
      key: "taxRate",
      width: "10%",
      sorter: (a, b) => a.taxRate - b.taxRate,
    },
    {
      title: "LCS RnD relief rate",
      dataIndex: "lcsRate",
      key: "lcsRate",
      width: "14%",
      sorter: (a, b) => a.lcsRate - b.lcsRate,
    },
    {
      title: "LCS vacc RnD relief rate",
      dataIndex: "lcsVaccRate",
      key: "lcsVaccRate",
      width: "14%",
      sorter: (a, b) => a.lcsVaccRate - b.lcsVaccRate,
    },
    {
      title: "LCS tax rate",
      dataIndex: "lTaxRate",
      key: "lTaxRate",
      width: "10%",
      sorter: (a, b) => a.lTaxRate - b.lTaxRate,
    },
    {
      title: "Min rate",
      dataIndex: "minRate",
      key: "minRate",
      width: "7%",
      sorter: (a, b) => (a.minRate ?? 0) - (b.minRate ?? 0),
    },
    {
      title: "Min amt",
      dataIndex: "minAmount",
      key: "minAmount",
      width: "7%",
      sorter: (a, b) => (a.minAmount ?? 0) - (b.minAmount ?? 0),
    },
    {
      title: "Action",
      dataIndex: "id",
      key: "Id",
      width: "5%",
      render: (_: any, record: any, index: any) => (
        <Link
          className="edit-btn"
          onClick={() => {
            if (record.id !== undefined || record.id !== null) {
              setDialog({
                type: "addRnDRate",
                params: { id: record.id, record: record },
              });
            } else {
              openNotification("error", "Id not Found")
            }
          }}
        >
          {
            isSubAdmin() ?
              <EyeFilled />
              :
              <EditTwoTone />
          }
        </Link>
      ),
    },
  ];

  React.useEffect(() => {
    const getRnDList = async () => {
      setLoading(true);
      let res = await CTConfiguration.getRnDRates();
      if (res.status) {
        setRnDRates(res.result);
        setLoading(false);
      } else {
        setLoading(false);
      }
    };
    getRnDList();
  }, [refresh])


  React.useEffect(() => {
    let data = [...rnDRatesList];

    const hasDateRange = params.fromDate && params.toDate;
    if (hasDateRange) {
      data = data.filter((item: CTConfigurationRnDRate) => {
        const start = dayjs(item.startDate);
        return (
          start.isSameOrAfter(params.fromDate, "day") &&
          start.isSameOrBefore(params.toDate, "day")
        );
      });
    }

    setFilterData(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.fromDate, params.toDate, rnDRatesList]);


  return (
    <>
      <Row>
        <Col span={24}>
          <Row gutter={[8, 8]} className="pb-3" align="middle">
            {/* ================= ADD ================= */}

            <Col xs={24} sm={12} md={5} lg={4} xl={3} xxl={2}>
              {
                !isSubAdmin() ?
                  <Button
                    type="primary"
                    block
                    onClick={() => {
                      setDialog({ type: "addRnDRate", params: {} });
                    }}
                  >
                    <PlusOutlined />
                    R&D rate
                  </Button>
                  : null
              }
            </Col>

            {/* ================= DATE ================= */}
            <Col xs={24} sm={12} md={{ span: 8, offset: 11 }} lg={{ span: 6, offset: 14 }} xl={{ span: 5, offset: 16 }} xxl={{ span: 4, offset: 18 }}>
              <CustomRangePicker
                value={[params.fromDate, params.toDate]}
                onChange={(dates) => {
                  setParams(prev => ({
                    ...prev,
                    fromDate: dates?.[0] || null,
                    toDate: dates?.[1] || null,
                  }));
                }}
              />
            </Col>
          </Row>
        </Col>
        <Col span={24}>
          <Table
            dataSource={filterdata?.length > 0 ? filterdata : []}
            columns={columns}
            size="small"
            className="aoa-custom-table border-top-table"
            // pagination={{
            //   defaultPageSize: 15,
            // }}
            loading={{ spinning: loading, indicator: <Spin indicator={<LoadingOutlined />} /> }}
            locale={{
              triggerDesc: "",
              triggerAsc: "",
              cancelSort: "",
            }}
            pagination={{
              defaultPageSize: 15,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} items`,
              showSizeChanger: true,
              total: filterdata?.length,
              pageSizeOptions: ["15", "25", "50", "100"],
              onChange: (page, pageSize) =>
                setPagination({ current: page, pageSize }),
            }}
            scroll={{ x: 1024 }}
          />
        </Col>
      </Row>
      {dialog.type === "addRnDRate" && (
        <AddRnDRates
          isViewOnly={isSubAdmin()}
          id={dialog.params?.id}
          record={dialog.params?.record}
          onDismiss={(confirmed: any) => {
            setDialog({ type: "", params: {} });
            if (confirmed) {
              setRefresh((x) => x = !x);
            }
          }}
        />
      )}
    </>
  );
};

export default RnDRates;
